import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, Edit, Trash2, ArrowUpRight } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { DraftInvoice } from '../services/draftInvoiceService'

interface ColumnActions {
  onEdit: (invoice: DraftInvoice) => void
  onDelete: (invoice: DraftInvoice) => void
  onConvert: (invoice: DraftInvoice) => void
}

export const getColumns = (actions: ColumnActions): ColumnDef<DraftInvoice>[] => [
  {
    accessorKey: 'invoice_number',
    header: 'Invoice #',
    cell: ({ row }) => {
      const invoiceNumber = row.getValue('invoice_number') as string
      return <span className="font-medium">{invoiceNumber || 'N/A'}</span>
    }
  },
  {
    accessorKey: 'customer_name',
    header: 'Customer',
    cell: ({ row }) => {
      const customerName = row.getValue('customer_name') as string
      return customerName || 'Walk-in Customer'
    }
  },
  {
    accessorKey: 'transaction_date',
    header: 'Date',
    cell: ({ row }) => {
      const date = row.getValue('transaction_date') as string
      return date ? format(new Date(date), 'dd MMM yyyy') : 'N/A'
    }
  },
  {
    accessorKey: 'total_amount',
    header: 'Amount',
    cell: ({ row }) => {
      const amount = row.getValue('total_amount') as number
      return <span className="font-medium">₹ {amount.toFixed(2)}</span>
    }
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as string
      const variant =
        status === 'draft' ? 'secondary' : status === 'converted' ? 'default' : 'destructive'
      return <Badge variant={variant}>{status}</Badge>
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => {
      const date = row.getValue('createdAt') as string
      return date ? format(new Date(date), 'dd MMM yyyy HH:mm') : 'N/A'
    }
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const invoice = row.original
      const isConverted = invoice.status === 'converted'

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!isConverted && (
              <>
                <DropdownMenuItem onClick={() => actions.onEdit(invoice)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => actions.onConvert(invoice)}>
                  <ArrowUpRight className="mr-2 h-4 w-4" />
                  Convert to Invoice
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem
              onClick={() => actions.onDelete(invoice)}
              className="text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  }
]
